/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useCallback, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

// Component imports
import { Col, Button, Row, Space, Spin, Card, notification, Grid, Dropdown } from 'antd';
import HRWizArticles from '../components/HRWiz/HRWizArticles';
import HRWizPreferences from '../components/HRWiz/HRWizPreferences';
import HRWizKnowledgeBaseSourcesTable from '../components/Tables/HRWizKnowledgeBaseSourcesTable';
import ChatSessionsTable from '../components/Tables/ChatSessionsTable';
import MissedQuestionsTable from '../components/Tables/MissedQuestionsTable';
import BotTestButtons from '../components/UI/BotTestButtons';
import OrganizationAnalytics, { ANALYTICS_TABS_KEYS } from '../components/Analytics/OrganizationAnalytics';
import { MenuOutlined } from '@ant-design/icons';

// Misc imports
import { useFetchBotsQuery } from '../store/bots/botsApi';
import { notificationEmitter } from '../store/notifications/notificationsThunks';
import { EMITTER_EVENTS, APP_NOTIFICATION_TYPES } from '../constants/main';
import { IS_HRWIZ_HEADER_DISABLED } from '../constants/env';
import { useTranslation } from 'react-i18next';
import { useInMicrosoftTeams } from '../utils/is/isInMicrosoftTeams';

// Constant declarations
export const HRWIZ_TABS = {
  ARTICLES: 'articles',
  EMPLOYEE_HANDBOOK: 'employee-handbook',
  PREFERENCES: 'preferences',
  SOURCES: 'sources',
  CONVERSATIONS: 'conversations',
  MISSED_QUESTIONS: 'missed-questions',
  ANALYTICS: 'analytics'
};

const getActiveKey = (searchParams) => {

  const urlTab = searchParams.get('tab');
  if (urlTab && Object.values(HRWIZ_TABS).includes(urlTab)) {
    return urlTab;
  }
  return HRWIZ_TABS.ARTICLES;
};

const TABS_CONFIG = [
  { key: HRWIZ_TABS.ARTICLES, label: 'Articles' },
  { key: HRWIZ_TABS.SOURCES, label: 'Sources' },
  { key: HRWIZ_TABS.CONVERSATIONS, label: 'Conversations' },
  { key: HRWIZ_TABS.MISSED_QUESTIONS, label: 'Missed Questions' },
  { key: HRWIZ_TABS.ANALYTICS, label: 'Analytics' }
];

const HRWizConsoleScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = Grid.useBreakpoint();
  const isMobileScreen = screens.xs && !screens.sm;
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const isInMicrosoftTeams = useInMicrosoftTeams();
  const { data: bots, isLoading: isLoadingBots } = useFetchBotsQuery(
    { orgId },
    {
      skip: !orgId,
      selectFromResult: (result) => ({
        ...result,
        data: result.data?.items?.[0]?.organizationId === orgId ? result.data : { items: [], total: 0 }
      })
    }
  );
  const [notificationApi, notificationContextHolder] = notification.useNotification();

  useEffect(() => {
    const contentChildren = document.querySelector('.content-children');

    if (IS_HRWIZ_HEADER_DISABLED) {
      if (contentChildren) {
        contentChildren.style.height = '100vh';
      }
    }
  }, []);

  useEffect(() => {
    if (bots?.items && bots.items.length > 0 && !tenantId) {
      const _bot = bots.items[0];
      navigate(`/org/${orgId}/bots/${_bot.id}`);
    }
  }, [bots, orgId, tenantId, isLoadingBots]);

  useEffect(() => {
    const handleNewNotification = (data) => {
      if (data.notificationType === APP_NOTIFICATION_TYPES.WARNING) {
        notificationApi.warning({
          message: t('Warning!'),
          description: data.message,
          placement: 'bottomRight'
        });
      }
    };

    notificationEmitter.on(EMITTER_EVENTS.NEW_NOTIFICATION, handleNewNotification);
    return () => {
      notificationEmitter.off(EMITTER_EVENTS.NEW_NOTIFICATION, handleNewNotification);
    };
  }, []);

  const handleTabChange = useCallback(
    (key) => {
      const entries = { ...Object.fromEntries(searchParams.entries()) };
      if (key === HRWIZ_TABS.ARTICLES) {
        delete entries.tab;
      } else {
        if (key === HRWIZ_TABS.CONVERSATIONS && 'order' in entries) {
          entries.order = entries.order.toUpperCase();
        } else if (key === HRWIZ_TABS.MISSED_QUESTIONS && 'order' in entries) {
          entries.order = entries.order.toLowerCase();
        }
        entries.tab = key;
      }
      setSearchParams(entries);
    },
    [searchParams, setSearchParams]
  );

  const renderTabContent = useCallback((activeKey) => {
    switch (activeKey) {
      case HRWIZ_TABS.ARTICLES:
        return <HRWizArticles />;
      case HRWIZ_TABS.SOURCES:
        return <HRWizKnowledgeBaseSourcesTable />;
      case HRWIZ_TABS.PREFERENCES:
        return <HRWizPreferences />;
      case HRWIZ_TABS.CONVERSATIONS:
        return <ChatSessionsTable hideAgentsPicker={true} />;
      case HRWIZ_TABS.MISSED_QUESTIONS:
        return <MissedQuestionsTable hideAgentsPicker={true} />;
      case HRWIZ_TABS.ANALYTICS:
        return (
          <OrganizationAnalytics
            showTabsWithKeys={[ANALYTICS_TABS_KEYS.SUMMARY, ANALYTICS_TABS_KEYS.MISSED_QUESTIONS]}
            hideDashboardSelection={true}
            hideAgentsPicker={true}
          />
        );
      default:
        return <HRWizArticles />;
    }
  }, [searchParams, setSearchParams]);

  if (isLoadingBots) {
    return (
      <Card>
        <Row className="h-100" align="middle" justify="center">
          <Spin />
        </Row>
      </Card>
    );
  }

  const activeKey = getActiveKey(searchParams);

  return (
    <>
      {notificationContextHolder}
      <Card>
        <Row justify="space-between" align="middle" className="mb-4">
          <Col>
            {isMobileScreen ? (
              <Dropdown
                menu={{
                  items: TABS_CONFIG.map((tab) => ({
                    key: tab.key,
                    label: tab.label,
                    onClick: () => handleTabChange(tab.key),
                    type: activeKey === tab.key ? 'primary' : 'default'
                  }))
                }}
                trigger={['click']}
                placement="bottomLeft"
              >
                <Button>
                  {TABS_CONFIG.find(tab => tab.key === activeKey)?.label || 'Articles'}
                  <MenuOutlined />
                </Button>
              </Dropdown>
            ) : (
              <Space size="small" align="center" wrap>
                {TABS_CONFIG.map((tab) => (
                  <a
                    href={`#?tab=${tab.key}`}
                    key={tab.key}
                    // type={activeKey === tab.key ? 'link' : 'text'}
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabChange(tab.key);
                    }}
                    className={`${activeKey === tab.key ? 'text-primary' : 'text-muted'} pr-6 fw-600 hover:text-dec-underline`}
                    // size={isMobileOrTabletSize ? 'small' : 'middle'}
                  >
                    {tab.label}
                  </a>
                ))}
              </Space>
            )}
          </Col>
          <Col>
            <BotTestButtons
              showPreviewButton={!isInMicrosoftTeams}
              showText={!isMobileOrTabletSize}
            />
          </Col>
        </Row>
        <div className="tab-content">
          {renderTabContent(activeKey)}
        </div>
      </Card>
    </>
  );
};

export default HRWizConsoleScreen;
